import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import '@v2/styles/tailwind.css';
import {
  PluginsTable,
  PluginDetails,
  DefaultMissionControlVersionHeader,
  PluginFilters,
} from './components';
import { MissionControlPlugin } from '@v2/types';
import { usePlugins, usePims, usePluginVersions, useTableFilters } from '@v2/hooks';
import { sanitizeQueryParam } from '@v2/lib';
import { SettingsPageLayout, DetailPanel, ListDetailView } from '@v2/layouts';
import { usePermissionsContext, Permissions } from '@v2/contexts';
import { faPuzzlePiece, faPen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ActionMenuItem } from '@v2/components';
import PluginForm from '@v2/features/PluginForm';

const MissionControlManagerPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const selectedPluginId = sanitizeQueryParam(searchParams.get('pluginId'));
  const { hasPermission } = usePermissionsContext();
  const canAddEditPlugin = hasPermission([Permissions.PluginManagerEdit]);

  const [supportedPimsId, setSupportedPimsId] = useState<string>('');
  const [includeInactive, setIncludeInactive] = useState<boolean>(false);
  const [isCreatePluginModalOpen, setIsCreatePluginModalOpen] = useState<boolean>(false);
  const [pluginToEdit, setPluginToEdit] = useState<MissionControlPlugin | null>(null);
  const {
    plugins,
    loading: pluginsLoading,
    error: pluginsError,
    refetch: refetchPlugins,
  } = usePlugins({
    pimsFilter: supportedPimsId || undefined,
    includeInactivePlugins: includeInactive,
  });

  const { pims } = usePims();

  const {
    filteredData,
    search,
    setSearch,
    sortConfig,
    handleSort,
  } = useTableFilters({
    data: plugins,
    searchKeys: ['name'],
    initialSort: { key: 'name', direction: 'asc' },
  });

  const selectedPlugin = selectedPluginId
    ? filteredData.find((p) => p.id === selectedPluginId)
    : null;

  const supportedPimsOptions = useMemo(
    () => [
      { value: '', label: 'Select PIMS' },
      ...pims
        .filter((p) => p.supportsConnectivity && p.name !== 'Other')
        .map((p) => ({ value: p.id, label: p.name })),
    ],
    [pims]
  );

  const {
    versions: pluginVersions,
    loading: versionsLoading,
    error: versionsError,
    refetch: refetchVersions,
  } = usePluginVersions(selectedPluginId);

  const handleSelectPlugin = (plugin: MissionControlPlugin) => {
    searchParams.set('pluginId', plugin.id);
    history.push({ search: searchParams.toString() });
  };

  const handleClosePanel = () => {
    searchParams.delete('pluginId');
    history.push({ search: searchParams.toString() });
  };

  const handleCreatePlugin = () => {
    setIsCreatePluginModalOpen(true);
  };

  const handleEditPlugin = () => {
    if (selectedPlugin) setPluginToEdit(selectedPlugin);
  };

  return (
    <SettingsPageLayout
      icon={faPuzzlePiece}
      title="Mission Control Manager"
      headerContent={<DefaultMissionControlVersionHeader canEditVersion={canAddEditPlugin} />}
      actionLabel={canAddEditPlugin ? 'Add Plugin' : undefined}
      onAction={canAddEditPlugin ? handleCreatePlugin : undefined}
    >
      <ListDetailView
        selectedId={selectedPluginId}
        list={
          <PluginsTable
            plugins={filteredData}
            loading={pluginsLoading}
            error={pluginsError}
            onSelectPlugin={handleSelectPlugin}
            selectedPluginId={selectedPluginId || undefined}
            sortConfig={sortConfig}
            onSortChange={handleSort}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name..."
            filterSlot={
              <PluginFilters
                supportedPimsOptions={supportedPimsOptions}
                supportedPimsId={supportedPimsId}
                onSupportedPimsChange={setSupportedPimsId}
                includeInactive={includeInactive}
                onIncludeInactiveChange={setIncludeInactive}
              />
            }
          />
        }
        detail={
          <DetailPanel
            title={selectedPlugin?.name ?? 'Plugin Details'}
            onClose={handleClosePanel}
            actions={
              canAddEditPlugin && selectedPlugin
                ? ([
                    {
                      key: 'edit',
                      label: 'Edit plugin',
                      icon: <FontAwesomeIcon icon={faPen} />,
                      onClick: handleEditPlugin,
                    },
                  ] as ActionMenuItem[])
                : undefined
            }
            loading={versionsLoading}
            loadingMessage="Loading plugin details..."
            error={versionsError}
            errorMessage="There was an error fetching the plugin versions."
            onRetry={() => {
              refetchPlugins();
              refetchVersions();
            }}
          >
            {selectedPlugin && (
              <PluginDetails
                plugin={selectedPlugin}
                versions={pluginVersions}
                versionsLoading={versionsLoading}
                pims={pims}
              />
            )}
          </DetailPanel>
        }
      />
      {isCreatePluginModalOpen && (
        <PluginForm
          existingPluginIds={plugins.map((p) => p.id)}
          onCancel={() => setIsCreatePluginModalOpen(false)}
          onCompleted={() => {
            setIsCreatePluginModalOpen(false);
            refetchPlugins();
          }}
        />
      )}
      {pluginToEdit && (
        <PluginForm
          plugin={pluginToEdit}
          onCancel={() => setPluginToEdit(null)}
          onCompleted={() => {
            setPluginToEdit(null);
            refetchPlugins();
          }}
        />
      )}
    </SettingsPageLayout>
  );
};

export default MissionControlManagerPage;
